import { ArrowUpRight } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import type { AudienceKind } from './AudienceCard'
import { Breadcrumb, type BreadcrumbItem } from './Breadcrumb'

/**
 * ArticleHeader — the sticky header block at the top of a docs site
 * article page. Implements plan section 22.3 (article page mockup):
 *
 *   Tenant admin › Applications › Create an application
 *   Create an application
 *   [Tenant admin] · Updated 2026-05-15 · 6 min read · Edit on Bitbucket →
 *
 * Composes Breadcrumb + an H1 + an inline meta line. The audience
 * badge is rendered as a small inline span (not the Badge primitive)
 * because Badge's variants don't include indigo; using the existing
 * accent token families (cyan / green / indigo) gives us colour-
 * coordinated tags consistent with AudienceCard and AudienceSwitcher.
 *
 * Per the abbreviation rule, the audience label defaults to the full
 * name. Apps that have already
 * established the audience context elsewhere on the page can override
 * via `audienceLabel`.
 */

const AUDIENCE_LABEL: Record<AudienceKind, string> = {
  developer: 'Developer',
  tenant_admin: 'Tenant admin',
  cp_admin: 'Control plane admin',
}

const AUDIENCE_BADGE_CLASS: Record<AudienceKind, string> = {
  developer: 'bg-ia-cyan-muted text-ia-cyan-text',
  tenant_admin: 'bg-ia-green-muted text-ia-green-text',
  cp_admin: 'bg-ia-indigo-muted text-ia-indigo-text',
}

export interface ArticleHeaderProps extends React.HTMLAttributes<HTMLElement> {
  /** Breadcrumb items — usually [audience, section, current topic]. */
  breadcrumb: BreadcrumbItem[]
  /** Article H1 title. */
  title: string
  /** Audience tag — drives the badge colour. */
  audience: AudienceKind
  /** Override the audience badge label. Defaults to the full name. */
  audienceLabel?: string
  /**
   * Last-updated date. ISO string (YYYY-MM-DD) recommended; pass-through
   * to the rendered label (no client-side relative-time computation —
   * keeps SSR output deterministic).
   */
  updatedAt?: string
  /** Estimated reading time in minutes. */
  estimatedRead?: number
  /**
   * URL for the article-action affordance (e.g. "Report issue",
   * "Suggest an edit"). Hidden when omitted. The exact label + target
   * is a docs-site policy choice; the docs repo is not public-editable
   * so a literal "Edit on Bitbucket" link is intentionally absent here.
   * Reserved for a future feedback affordance to be designed.
   */
  actionUrl?: string
  /** Visible label for `actionUrl`. Required when actionUrl is set. */
  actionLabel?: string
}

export const ArticleHeader = React.forwardRef<HTMLElement, ArticleHeaderProps>(
  (
    {
      breadcrumb,
      title,
      audience,
      audienceLabel,
      updatedAt,
      estimatedRead,
      actionUrl,
      actionLabel,
      className,
      ...rest
    },
    ref,
  ) => {
    const label = audienceLabel ?? AUDIENCE_LABEL[audience]

    // Meta parts: collapse empty values so the dot-separators only
    // render between non-empty segments.
    const metaParts: React.ReactNode[] = []
    if (updatedAt) {
      metaParts.push(<span key="updated">Updated {updatedAt}</span>)
    }
    if (typeof estimatedRead === 'number') {
      metaParts.push(
        <span key="read">
          {estimatedRead} min read
        </span>,
      )
    }
    if (actionUrl && actionLabel) {
      metaParts.push(
        <a
          key="action"
          href={actionUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-ia-green-text font-medium hover:underline whitespace-nowrap"
        >
          {actionLabel}
          <ArrowUpRight size={12} aria-hidden="true" />
        </a>,
      )
    }

    return (
      <header
        ref={ref}
        className={cn('flex flex-col gap-3', className)}
        {...rest}
      >
        <Breadcrumb items={breadcrumb} />

        <h1 className="text-2xl font-semibold text-ia-text tracking-tight m-0">
          {title}
        </h1>

        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-caption text-ia-text-secondary">
          <span
            className={cn(
              'inline-flex items-center rounded-pill px-2 py-0.5 text-[11px] font-medium',
              AUDIENCE_BADGE_CLASS[audience],
            )}
          >
            {label}
          </span>
          {metaParts.map((node, i) => (
            <React.Fragment key={i}>
              <span aria-hidden="true" className="text-ia-text-tertiary">
                ·
              </span>
              {node}
            </React.Fragment>
          ))}
        </div>
      </header>
    )
  },
)
ArticleHeader.displayName = 'ArticleHeader'
